import { Suspense, lazy } from "react"
import {
  Navigate,
  createBrowserRouter,
  matchRoutes,
  useLocation,
  type RouteObject,
} from "react-router"

import { AuthLayout } from "./layouts/AuthLayout"
import { DocumentLayout } from "./layouts/DocumentLayout"
import { PhoneScreenGuard } from "./layouts/PhoneScreenGuard"
import { RouteErrorBoundary } from "./error-boundary"
import { featureRoutes, routeRedirects, type RouteDef } from "./routes"
import { LoadingState } from "@/components/kit/states"
import { FullPageLoader } from "@/components/kit/FullPageLoader"
import { RequireSession } from "@/auth/RequireSession"
import { hasSessionHint } from "@/auth/session-hint"
import { RequirePermission } from "@/auth/RequirePermission"
import { authRoutes } from "@/features/auth"
import { PUBLIC_PAGES, PageScreen } from "@/features/pages"

/**
 * The shell (sidebar, menus, tooltips: most of Radix) is its own chunk, so an
 * anonymous visitor's sign-in never downloads or runs it (NFR-DSH-01, Task
 * 0.6). It is not preloaded from index.html: its 36 KB would share the
 * bandwidth of the shell's download on the anonymous landing, the one the LCP
 * gate measures. A returning user (the session hint) starts it at boot, in
 * parallel with the refresh, rather than after it (Task 7.2: ~0.4 s on the
 * mobile profile).
 */
const loadShell = () => import("./layouts/AppShell")
if (hasSessionHint()) {
  void loadShell()
  // And the screen the address names, when it is a lazy screen with a namespace.
  const hit = matchRoutes(
    featureRoutes.map((r) => ({ path: r.path, route: r })),
    window.location.pathname,
  )?.[0]?.route.route.element
  const preload = (hit as { preload?: () => Promise<unknown> } | undefined)?.preload
  if (preload) void preload()
}
const AppShell = lazy(async () => ({ default: (await loadShell()).AppShell }))
const NotFoundScreen = lazy(() => import("./NotFoundScreen"))

/** A backend notice link to an old path (DW-06): same destination, query kept. */
function Redirect({ to }: { to: string }) {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: to, search, hash }} replace />
}

/**
 * Every route is lazy, so each slice is its own chunk (FR-DSH-SHL-08). The
 * fallback is a skeleton shaped like the destination, never a spinner on an
 * empty page. `RequirePermission` decides each one from `me` per render
 * rather than by rebuilding the router per mode or permission set (DW-88):
 * a revoked permission or a redeploy that changes the mode takes effect on
 * the next `me`, without a reload.
 */
function toRouteObject(r: RouteDef): RouteObject {
  const Element = r.element
  return {
    path: r.path,
    element: (
      <RequirePermission route={r} notFound={lazyElement(NotFoundScreen)}>
        <PhoneScreenGuard phoneSafe={r.phoneSafe}>
          <Suspense fallback={<LoadingState variant="detail" />}>
            <Element />
          </Suspense>
        </PhoneScreenGuard>
      </RequirePermission>
    ),
  }
}

const lazyElement = (Screen: ReturnType<typeof lazy>) => (
  <Suspense fallback={<LoadingState variant="detail" />}>
    <Screen />
  </Suspense>
)

/**
 * Public routes render in `AuthLayout`; everything else sits behind the
 * session gate in `AppShell`, so an anonymous visitor on any other path --
 * bare "/", a typo, a stale bookmark -- is sent to sign-in rather than shown
 * the shell chrome (LLD §7.1).
 */
export const appRoutes: RouteObject[] = [
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: authRoutes,
  },
  // The content pages (DV-158): open to everyone, signed in or not.
  {
    element: <DocumentLayout />,
    errorElement: <RouteErrorBoundary />,
    children: PUBLIC_PAGES.map((p) => ({
      path: p.path,
      element: (
        <Suspense fallback={<LoadingState variant="detail" />}>
          <PageScreen slug={p.slug} />
        </Suspense>
      ),
    })),
  },
  {
    element: (
      <RequireSession>
        <Suspense fallback={<FullPageLoader />}>
          <AppShell />
        </Suspense>
      </RequireSession>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      ...featureRoutes.map(toRouteObject),
      ...routeRedirects.map((r) => ({
        path: r.from,
        element: (
          <RequirePermission route={{ money: r.money }} notFound={lazyElement(NotFoundScreen)}>
            <Redirect to={r.to} />
          </RequirePermission>
        ),
      })),
      { path: "*", element: lazyElement(NotFoundScreen) },
    ],
  },
]

export function createAppRouter() {
  return createBrowserRouter(appRoutes)
}
